import type { IAugmentedJQuery, IController, ITimeoutService } from 'angular';
import template from './export-dialog.component.html';
import type { PrintingService } from '../printing.service';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';
import type { ThemeService } from '../../Theme/mx-theme.service';
import type { Theme, ThemeRecord } from '../../Theme/theme.contracts';
import type { ThemeConfig } from '../../Theme/mx-theme.directive';

export type Orientation = 'portrait' | 'landscape';
export type ExportType = 'png' | 'jpeg' | 'pdf';

export interface ExportConfig {
	type: ExportType;
	orientation: Orientation;
	width: number;
	contentNode: HTMLElement;
	headerNode?: HTMLElement;
	footerNode?: HTMLElement;
	themeId?: string;
	isContrastTheme?: boolean;
	pageHeight?: number;
}

export class ExportDialogController implements IController {
	type: ExportType = 'pdf';
	expectedFileType?: ExportType;
	orientation: Orientation = 'portrait';
	readonly width = 2000;
	includeHeaderFooter = true;
	title = '';
	previewHeaderHtml = '';
	previewFooterHtml = '';
	scale = 100;
	scaleStyles: HTMLStyleElement;
	onConfigChanged!: (props: { config: ExportConfig }) => void;
	themes: ThemeRecord[] = [];
	themeConfig?: ThemeConfig;

	static readonly $inject = ['$element', '$timeout', 'mx.sb.Printing.PrintingService', 'mx.shell.EventService', 'mx.app.ThemeService'];
	constructor(
		private readonly $element: IAugmentedJQuery,
		private readonly $timeout: ITimeoutService,
		private readonly printingService: PrintingService,
		private readonly eventService: EventService,
		private readonly themeService: ThemeService
	) {
		this.scaleStyles = this.initScaleStyles();
		this.setScaledStyles();
	}

	async $onInit() {
		if (this.expectedFileType) {
			this.type = this.expectedFileType;
		}
		this.themes = await this.themeService.getAvailableThemes();
		const selectedTheme = await this.themeService.getCurrentTheme();
		this.themeConfig = { themeId: selectedTheme.id, isContrastTheme: selectedTheme.isContrastTheme };

		await this.loadMarkup(selectedTheme);

		setTimeout(() => {
			this.onConfigChanged({ config: this.getExportConfig() });
		}, 50);
	}

	private async loadMarkup(theme: Theme) {
		const { printingHeader, printingFooter } = (await this.printingService.getCurrentApplicationPrintingMarkup(this.title, theme['applicationLogoUrl'])) || {};

		this.previewHeaderHtml = printingHeader ?? '';
		this.previewFooterHtml = printingFooter ?? '';
	}

	private initScaleStyles() {
		const styleTag = document.createElement('style');
		styleTag.id = `mx-export-dialog-styles_${Math.random()}`;
		styleTag.type = 'text/css';
		this.$element.append(styleTag);

		styleTag.innerHTML = '';
		return styleTag;
	}

	private getDocSizes() {
		const docWidth = this.type === 'pdf' ? (this.orientation === 'portrait' ? 447 : 631) : 700;
		const docHeight = this.type === 'pdf' ? (this.orientation === 'portrait' ? 631 : 447) : 700;
		const scale = this.type === 'pdf' ? docWidth / 2000 : 0.35;
		return { docWidth, docHeight, scale };
	}

	private setScaledStyles() {
		this.scaleStyles.innerHTML = '';
		const { docWidth, docHeight, scale } = this.getDocSizes();

		this.scaleStyles.appendChild(
			document.createTextNode(
				`.mx-export-dialog--preview-document-scaled {
					transform-origin: left top;
					transform: scale(${scale});
					width: ${docWidth / scale}px;
					height: ${docHeight}px;
					max-width: none;
					position: absolute;
				}`
			)
		);
		this.eventService.dispatchEvent(EventCodes.LayoutChanged);
	}

	private getExportConfig(): ExportConfig {
		const docSizes = this.getDocSizes();
		const headerNode = this.$element.find('.mx-export-dialog--preview-header')[0];
		const footerNode = this.$element.find('.mx-export-dialog--preview-footer')[0];

		const pageHeight = this.type === 'pdf' ? docSizes.docHeight - (headerNode?.offsetHeight ?? 0) - (footerNode?.offsetHeight ?? 0) : undefined;

		return {
			type: this.type,
			orientation: this.orientation,
			width: this.width,
			contentNode: this.$element.find('.mx-export-dialog--preview')[0],
			headerNode,
			footerNode,
			themeId: this.themeConfig?.themeId,
			isContrastTheme: this.themeConfig?.isContrastTheme,
			pageHeight,
		};
	}

	private onTypeChange() {
		this.$timeout(() => {
			this.setScaledStyles();
			this.onConfigChanged({ config: this.getExportConfig() });
		});
	}

	private onIncludeHeaderChange() {
		this.$timeout(() => {
			this.onConfigChanged({ config: this.getExportConfig() });
		});
	}

	private onOrientationChange() {
		this.$timeout(() => {
			this.setScaledStyles();
			this.onConfigChanged({ config: this.getExportConfig() });
		});
	}

	private async onThemeChanged(themeId: string) {
		this.themeConfig = { isContrastTheme: this.themeConfig?.isContrastTheme ?? false, themeId };
		const selectedTheme = await this.themeService.getTheme(this.themeConfig.themeId, this.themeConfig.isContrastTheme);
		await this.loadMarkup(selectedTheme);
		this.onConfigChanged({ config: this.getExportConfig() });
	}

	private async onContrastThemeChanged(isContrastTheme: boolean) {
		if (!this.themeConfig?.themeId) {
			const selectedTheme = await this.themeService.getCurrentTheme();
			this.themeConfig = { themeId: selectedTheme.id, isContrastTheme };
		} else {
			this.themeConfig = { themeId: this.themeConfig.themeId, isContrastTheme };
		}
		const selectedTheme = await this.themeService.getTheme(this.themeConfig.themeId, this.themeConfig.isContrastTheme);
		await this.loadMarkup(selectedTheme);
		this.onConfigChanged({ config: this.getExportConfig() });
	}
}

export const ExportDialogComponent = {
	name: 'mxExportDialog',
	controllerAs: 'vm',
	controller: ExportDialogController,
	transclude: true,
	bindings: {
		title: '<label',
		onConfigChanged: '&',
		expectedFileType: '@fileType',
		loaded: '<',
	},
	template,
};
